import { NetworkStatus } from "@apollo/client";
import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Menu,
  MenuItem,
  MenuList,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useBreakpointValue,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiArrowDownTray, HiChevronDown } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Alert,
  BasicTooltip,
  Button,
  IconButton,
  LoadingIndicator,
  MenuButton,
} from "../../../components";
import useDebounce from "../../../hooks/useDebounce";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import {
  useEnumSearchParam,
  useSearchParam,
} from "../../../hooks/useSearchParam";
import { getErrorInfo, isCustomError } from "../../../utils/apollo/error";
import {
  AiInterviewerFragment,
  AiInterviewInvitationStatus,
  AiInterviewSort,
  AiInterviewStatus,
  SortDirection,
  useAiInterviewCandidatesCsvLazyQuery,
  useAiInterviewInvitationsQuery,
  useAiInterviewInvitationStatusCountsQuery,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import { useBHScreenPermission } from "../../hooks/useBHScreenPermission";
import { AiInterviewerCandidateScores } from "./AiInterviewerCandidateScores";
import AiInterviewerCandidatesFilters, {
  AiInterviewerCandidatesFiltersBody,
} from "./AiInterviewerCandidatesFilters";
import {
  AiInterviewerListInterviewItemType,
  CandidateListItemState,
} from "./AiInterviewerListInterviewItem";
import { AiInterviewerListInterviews } from "./AiInterviewerListInterviews";

type AiInterviewerCandidatesProps = {
  aiInterviewer: Pick<
    AiInterviewerFragment,
    | "id"
    | "name"
    | "currentVersion"
    | "hasScorableQuestions"
    | "currentUserCanView"
    | "currentUserCanEdit"
    | "currentUserCanManage"
  >;
};

type AiInterviewerCandidatesListProps = {
  aiInterviewerId: string;
  aiInterviewerName: string;
  hasScorableQuestions: boolean;
  canEdit: boolean;
  showIntegritySignals: boolean;
};

const PAGE_LIMIT = 50;

/**
 * Determine the state of a candidate based on their invitation/interview data
 */
function getCandidateState(
  hasStartedInterview: boolean,
  interviewStatus?: AiInterviewStatus,
  hasEvaluation?: boolean,
  isIncomplete?: boolean
): CandidateListItemState {
  // No interview started yet
  if (!hasStartedInterview) {
    return CandidateListItemState.WaitingToStart;
  }

  // Interview in progress (CREATED or STARTED status)
  if (
    interviewStatus === AiInterviewStatus.Created ||
    interviewStatus === AiInterviewStatus.Started
  ) {
    return CandidateListItemState.InProgress;
  }

  // Interview has error
  if (interviewStatus === AiInterviewStatus.Error) {
    return CandidateListItemState.SummaryFailure;
  }

  // Interview finished
  if (interviewStatus === AiInterviewStatus.Finished) {
    // Has evaluation - completed
    if (hasEvaluation && !isIncomplete) {
      return CandidateListItemState.Completed;
    }
    // No evaluation yet - summary pending
    // (We treat incomplete interviews as completed with evaluation since isIncomplete is set during evaluation)
    return CandidateListItemState.SummaryPending;
  }

  // Default fallback
  return CandidateListItemState.SummaryPending;
}

const MemoizedAiInterviewerCandidatesFilters = React.memo(
  AiInterviewerCandidatesFilters
);

const AiInterviewerCandidatesList: React.FC<
  AiInterviewerCandidatesListProps
> = ({
  aiInterviewerId,
  aiInterviewerName,
  hasScorableQuestions,
  canEdit,
  showIntegritySignals,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [fetchCsv, { loading: csvLoading }] =
    useAiInterviewCandidatesCsvLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        if (data?.aiInterviewCandidatesCsv?.url) {
          const { url } = data.aiInterviewCandidatesCsv;
          const downloadLink = document.createElement("a");
          downloadLink.style.display = "none";
          downloadLink.href = url;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          toast({
            title: "Export failed",
            description: "Unable to generate CSV. Please try again.",
            status: "error",
            duration: 5000,
          });
        }
      },
      onError() {
        toast({
          title: "Export failed",
          description: "An error occurred while generating the CSV.",
          status: "error",
          duration: 5000,
        });
      },
    });
  const [sort, setSort] = useEnumSearchParam<AiInterviewSort>("sort");
  const [direction, setDirection] =
    useEnumSearchParam<SortDirection>("direction");
  const [keywordFilter, setKeywordFilter] = useSearchParam("keyword", "");
  const [statusFilters, setStatusFilters] = useSearchParam("statuses", "");
  const [currentPage, setCurrentPage] = useState(1);
  const [isQueryChanging, setIsQueryChanging] = useState(false);
  const wasSentinelInViewRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [listRoot, setListRoot] = useState<HTMLDivElement | null>(null);
  const previousQueryKeyRef = useRef<string>("");
  const sentinelInView = useIntersectionObserver(sentinelRef, {
    root: listRoot,
    rootMargin: "20%",
  });
  const setListRef = (node: HTMLDivElement | null): void => {
    setListRoot(node);
  };
  const isMobile = useBreakpointValue({ base: true, md: false });
  const debouncedKeyword = useDebounce(keywordFilter || "", 2000);
  // On mobile, don't debounce - filters are applied explicitly via "Apply Filters" button
  // On desktop, debounce for live search as user types
  const effectiveKeyword =
    keywordFilter && keywordFilter.trim().length > 0
      ? isMobile
        ? keywordFilter
        : debouncedKeyword
      : "";

  // Show spinner when keyword has changed but debounce hasn't fired yet (desktop only)
  const isSearching =
    !isMobile &&
    (keywordFilter?.trim() || "") !== (effectiveKeyword?.trim() || "");

  // Prefer URL params; otherwise fall back to default values
  const uiSort = sort ?? AiInterviewSort.Date;
  const uiDirection = direction ?? SortDirection.Desc;

  // Parse status filters from URL param (comma-separated)
  // Default: empty (all unchecked = show all). Empty = show all.
  // Statuses are stored in camelCase format in the URL
  const statuses = useMemo(() => {
    if (!statusFilters) return []; // Default: all unchecked (show all)
    return statusFilters.split(",").filter(Boolean);
  }, [statusFilters]);

  const statusFiltersForQuery = useMemo(() => {
    const statusMap: Record<string, AiInterviewInvitationStatus> = {
      invited: AiInterviewInvitationStatus.Invited,
      inProgress: AiInterviewInvitationStatus.InProgress,
      summaryFailure: AiInterviewInvitationStatus.SummaryFailure,
      completed: AiInterviewInvitationStatus.Completed,
    };

    return statuses
      .map((status) => statusMap[status])
      .filter((status): status is AiInterviewInvitationStatus => !!status);
  }, [statuses]);

  const handleStatusesChange = useCallback(
    (newStatuses: string[]) => {
      setIsQueryChanging(true);
      setStatusFilters(newStatuses.length > 0 ? newStatuses.join(",") : "");
      setCurrentPage(1);
    },
    [setStatusFilters]
  );

  // Batched filter apply for mobile - updates both keyword and statuses in a single navigation
  const handleApplyFilters = useCallback(
    (newKeyword: string, newStatuses: string[]) => {
      setIsQueryChanging(true);
      setCurrentPage(1);

      // Single navigate() call with both params - this is atomic, triggers one query
      const params = new URLSearchParams(location.search);
      if (newKeyword.trim()) {
        params.set("keyword", newKeyword);
      } else {
        params.delete("keyword");
      }
      if (newStatuses.length > 0) {
        params.set("statuses", newStatuses.join(","));
      } else {
        params.delete("statuses");
      }
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  // Fetch status counts separately - only refetch when filters change, not on pagination
  const { data: statusCountsData } = useAiInterviewInvitationStatusCountsQuery({
    variables: {
      aiInterviewerId,
    },
    // Refetch when filters change, but not on pagination
    fetchPolicy: "cache-and-network",
  });

  // Use the invitations query which includes all candidates (with and without interviews)
  // Backend handles sorting, so we pass sort/direction params
  const {
    data: invitationsData,
    previousData: previousInvitationsData,
    loading: invitationsLoading,
    error: invitationsError,
    fetchMore,
    networkStatus,
  } = useAiInterviewInvitationsQuery({
    variables: {
      aiInterviewerId,
      keyword: effectiveKeyword.trim() || undefined,
      statuses: statusFiltersForQuery.length
        ? statusFiltersForQuery
        : undefined,
      sort: uiSort,
      direction: uiDirection,
      pagination: {
        page: 1,
        limit: PAGE_LIMIT,
      },
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // Create a query key to track when variables change
  const queryKey = useMemo(
    () =>
      JSON.stringify({
        keyword: effectiveKeyword.trim(),
        statuses: statusFiltersForQuery,
        sort: uiSort,
        direction: uiDirection,
      }),
    [effectiveKeyword, statusFiltersForQuery, uiSort, uiDirection]
  );

  // Track if query variables have changed (for showing loading state)
  // isQueryChanging is set immediately by filter handlers for instant feedback,
  // and is also set via effect when queryKey changes (for other triggers like sort)
  const pendingQueryKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (queryKey !== previousQueryKeyRef.current) {
      // Query variables changed - mark as loading and track which query we're waiting for
      pendingQueryKeyRef.current = queryKey;
      previousQueryKeyRef.current = queryKey;
      setIsQueryChanging(true);
    }
  }, [queryKey]);

  // Clear loading state only when query completes AND we're not still loading
  useEffect(() => {
    if (
      isQueryChanging &&
      !invitationsLoading &&
      networkStatus === NetworkStatus.ready &&
      pendingQueryKeyRef.current === queryKey
    ) {
      pendingQueryKeyRef.current = null;
      setIsQueryChanging(false);
    }
  }, [isQueryChanging, invitationsLoading, networkStatus, queryKey]);

  const resolvedInvitationsData = invitationsData ?? previousInvitationsData;
  const invitations =
    resolvedInvitationsData?.aiInterviewInvitations?.results ?? [];
  const totalRows =
    resolvedInvitationsData?.aiInterviewInvitations?.pageInfo?.totalRows ??
    undefined;
  const lastKnownTotalRowsRef = useRef(0);
  const stableTotalRows = useMemo(() => {
    if (typeof totalRows === "number") {
      lastKnownTotalRowsRef.current = totalRows;
      return totalRows;
    }
    return lastKnownTotalRowsRef.current;
  }, [totalRows]);
  const hasNextPage = invitations.length < stableTotalRows;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  // Get status counts from separate query (true totals)
  const backendCounts = statusCountsData?.aiInterviewInvitationStatusCounts;
  const lastKnownStatusCountsRef = useRef<{
    invited: number;
    inProgress: number;
    summaryFailure: number;
    completed: number;
  }>({
    invited: 0,
    inProgress: 0,
    summaryFailure: 0,
    completed: 0,
  });
  const statusCounts = useMemo(() => {
    if (backendCounts) {
      const nextCounts = {
        invited: backendCounts.invited ?? 0,
        inProgress: backendCounts.inProgress ?? 0,
        summaryFailure: backendCounts.summaryFailure ?? 0,
        completed: backendCounts.completed ?? 0,
      };
      lastKnownStatusCountsRef.current = nextCounts;
      return nextCounts;
    }
    return lastKnownStatusCountsRef.current;
  }, [
    backendCounts?.invited,
    backendCounts?.inProgress,
    backendCounts?.summaryFailure,
    backendCounts?.completed,
  ]);

  // Calculate total available for current status filters
  const totalAvailableForFilters = stableTotalRows;
  const totalUnfilteredCount = useMemo(() => {
    return (
      statusCounts.invited +
      statusCounts.inProgress +
      statusCounts.summaryFailure +
      statusCounts.completed
    );
  }, [statusCounts]);

  const hasAppliedFilters =
    (keywordFilter?.trim().length ?? 0) > 0 || statuses.length > 0;

  const handleResetFilters = useCallback(() => {
    setKeywordFilter("");
    handleStatusesChange([]);
  }, [handleStatusesChange, setKeywordFilter]);

  // Helper function to get status label
  const getStatusLabel = useCallback((statusValue: string) => {
    const statusMap: Record<string, string> = {
      invited: "Invited",
      inProgress: "In Progress",
      summaryFailure: "Error",
      completed: "Completed",
    };
    return statusMap[statusValue] ?? statusValue;
  }, []);

  const handleRemoveKeywordFilter = useCallback(() => {
    setKeywordFilter("");
  }, [setKeywordFilter]);

  const handleRemoveStatusFilter = useCallback(
    (statusToRemove: string) => {
      handleStatusesChange(statuses.filter((s) => s !== statusToRemove));
    },
    [statuses, handleStatusesChange]
  );

  // Transform invitations data into list items with proper state
  // Backend already sorts the data, so we don't need to sort again
  const candidates = useMemo(() => {
    return invitations.map((invitation): AiInterviewerListInterviewItemType => {
      const { latestInterview: interview, hasStartedInterview } = invitation;
      const interviewStatus = interview?.status;
      const hasEvaluation = !!interview?.evaluation;
      const isIncomplete = interview?.isIncomplete ?? false;

      const state = getCandidateState(
        hasStartedInterview,
        interviewStatus,
        hasEvaluation,
        isIncomplete
      );

      const signals = interview?.integritySignals;
      const detectedSignals = signals?.filter((s) => s.detected) ?? [];

      return {
        id: invitation.id,
        invitationId: invitation.id,
        invitationLink: invitation.url ?? undefined,
        isInvitationExpired: invitation.isExpired,
        callId: interview?.callId,
        callCreatedAt: interview?.createdAt ?? invitation.createdAt,
        candidateName:
          invitation.candidate?.fullName ||
          invitation.candidateName ||
          "Unnamed candidate",
        positionTitle: invitation.position?.displayTitle || "Unnamed position",
        aiInterviewerName,
        evaluation: isIncomplete ? null : interview?.evaluation ?? null,
        isIncomplete,
        hasRedactedQuestions: interview?.hasRedactedQuestions ?? false,
        state,
        interviewStatus,
        hasScorableQuestions,
        integritySignals:
          detectedSignals.length > 0
            ? { detectedCount: detectedSignals.length }
            : null,
        showIntegritySignals,
      };
    });
  }, [invitations, hasScorableQuestions, showIntegritySignals]);

  // Determine empty state message based on filter state
  const emptyMessage = useMemo(() => {
    if (
      hasAppliedFilters &&
      candidates.length === 0 &&
      totalUnfilteredCount > 0
    ) {
      return "No interviews match your filter criteria. Undo or reset your filters.";
    }
    return undefined; // Use default message
  }, [hasAppliedFilters, candidates.length, totalUnfilteredCount]);

  const loading =
    isQueryChanging ||
    invitationsLoading ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch;
  // Only show spinner when loading AND we have no data yet (initial load or filter change)
  // During fetchMore, keep the list mounted to preserve scroll position
  const showLoadingSpinner = loading && candidates.length === 0;
  // Show blur overlay on mobile when filters are loading but we have existing data
  const showMobileFilterLoadingOverlay =
    isMobile && loading && candidates.length > 0 && !isFetchingMore;
  const error = invitationsError;

  useEffect(() => {
    setCurrentPage(1);
    wasSentinelInViewRef.current = false;
  }, [
    aiInterviewerId,
    effectiveKeyword,
    uiSort,
    uiDirection,
    statusFiltersForQuery,
  ]);

  useEffect(() => {
    if (
      !sentinelInView ||
      wasSentinelInViewRef.current ||
      !hasNextPage ||
      isFetchingMore ||
      loading
    ) {
      wasSentinelInViewRef.current = sentinelInView;
      return;
    }
    const nextPage = currentPage + 1;
    fetchMore({
      variables: {
        aiInterviewerId,
        keyword: effectiveKeyword.trim() || undefined,
        statuses: statusFiltersForQuery.length
          ? statusFiltersForQuery
          : undefined,
        sort: uiSort,
        direction: uiDirection,
        pagination: {
          page: nextPage,
          limit: PAGE_LIMIT,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.aiInterviewInvitations) return prev;
        return {
          ...prev,
          aiInterviewInvitations: {
            ...fetchMoreResult.aiInterviewInvitations,
            results: [
              ...(prev.aiInterviewInvitations?.results ?? []),
              ...(fetchMoreResult.aiInterviewInvitations.results ?? []),
            ],
          },
        };
      },
    });
    setCurrentPage(nextPage);
    wasSentinelInViewRef.current = sentinelInView;
  }, [
    aiInterviewerId,
    currentPage,
    effectiveKeyword,
    fetchMore,
    hasNextPage,
    isFetchingMore,
    loading,
    sentinelInView,
    statusFiltersForQuery,
    uiDirection,
    uiSort,
  ]);

  const listContent = (() => {
    if (error) {
      const { message, code } = getErrorInfo(error);
      const errorMessage = isCustomError(error)
        ? message
        : code === "FORBIDDEN"
        ? "You are not authorized to access this page."
        : "An error occurred while loading the page.";

      return (
        <Alert
          status="error"
          title="Error"
          flex="1"
          description={errorMessage}
        />
      );
    }

    // Only show spinner when loading and we have no data yet (initial load or filter change)
    // During fetchMore, keep the list mounted to preserve scroll position
    if (showLoadingSpinner) {
      return <LoadingIndicator m="auto" pos="relative" top="-25%" />;
    }

    return (
      <AiInterviewerListInterviews
        interviews={candidates}
        flex="1"
        canEdit={canEdit}
        scrollRef={setListRef}
        emptyMessage={emptyMessage}
        showLoadingOverlay={showMobileFilterLoadingOverlay}
        footer={
          <Box ref={sentinelRef} py="3" w="100%">
            {isFetchingMore && <LoadingIndicator size="sm" m="auto" />}
          </Box>
        }
      />
    );
  })();

  const sortOptions: {
    sort: AiInterviewSort;
    direction: SortDirection;
    label: string;
  }[] = [
    {
      sort: AiInterviewSort.Score,
      direction: SortDirection.Desc,
      label: "Score ↓",
    },
    {
      sort: AiInterviewSort.Score,
      direction: SortDirection.Asc,
      label: "Score ↑",
    },
    {
      sort: AiInterviewSort.Alphabetical,
      direction: SortDirection.Asc,
      label: "Alphabetical ↑",
    },
    {
      sort: AiInterviewSort.Alphabetical,
      direction: SortDirection.Desc,
      label: "Alphabetical ↓",
    },
    {
      sort: AiInterviewSort.Date,
      direction: SortDirection.Desc,
      label: "Date ↓",
    },
    {
      sort: AiInterviewSort.Date,
      direction: SortDirection.Asc,
      label: "Date ↑",
    },
  ];

  const activeSortLabel =
    sortOptions.find((o) => o.sort === uiSort && o.direction === uiDirection)
      ?.label ?? "Score ↓";

  const sortSelect = (
    <Menu>
      <MenuButton
        as={Button}
        variant="outline"
        size="sm"
        borderColor="gray.200"
        borderRadius="md"
        bg="white"
        fontWeight="normal"
        _hover={{ borderColor: "gray.300", bg: "gray.50" }}
        _active={{ bg: "gray.50" }}
        transition="all 150ms ease"
      >
        <HStack spacing="2">
          <Text fontSize="sm" color="gray.900" whiteSpace="nowrap">
            Sort: {activeSortLabel}
          </Text>
          <Icon as={HiChevronDown} boxSize="4" color="gray.500" />
        </HStack>
      </MenuButton>
      <MenuList minW="180px" py="1" shadow="lg" borderRadius="md">
        {sortOptions.map((option) => {
          const isActive =
            option.sort === uiSort && option.direction === uiDirection;
          return (
            <MenuItem
              key={`${option.sort}-${option.direction}`}
              fontSize="sm"
              fontWeight={isActive ? "semibold" : "normal"}
              color={isActive ? "blue.600" : "gray.700"}
              bg={isActive ? "blue.50" : "transparent"}
              _hover={{ bg: isActive ? "blue.50" : "gray.50" }}
              onClick={() => {
                setSort(option.sort);
                setDirection(option.direction);
              }}
            >
              {option.label}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );

  const csvButton = canEdit ? (
    <BasicTooltip label="Download CSV">
      <IconButton
        aria-label="Download CSV"
        icon={<Icon as={HiArrowDownTray} boxSize="5" color="gray.800" />}
        isLoading={csvLoading}
        size="sm"
        variant="outline"
        borderColor="gray.300"
        borderRadius="md"
        _hover={{ bg: "gray.50" }}
        onClick={() => {
          toast({
            title: "Generating CSV...",
            description: "This may take a moment.",
            status: "info",
            duration: 3000,
          });
          fetchCsv({
            variables: {
              aiInterviewerId,
            },
          });
        }}
      />
    </BasicTooltip>
  ) : null;

  return isMobile ? (
    // Mobile layout
    <Flex gap="6" flex="1" minH="0" direction="column" overflow="auto">
      <Box bg="gray.50" borderRadius="lg" p={4} flexShrink={0}>
        <Flex direction="column" gap={3}>
          <HStack justify="space-between" alignItems="center">
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {totalAvailableForFilters} of {totalUnfilteredCount} candidates
            </Text>
            <MemoizedAiInterviewerCandidatesFilters
              keyword={keywordFilter || ""}
              statuses={statuses}
              onKeywordChange={setKeywordFilter}
              onStatusesChange={handleStatusesChange}
              onApplyFilters={handleApplyFilters}
              totalCount={totalAvailableForFilters}
              isMobile
              statusCounts={statusCounts}
              isSearching={isSearching}
            />
          </HStack>
          {hasAppliedFilters && (
            <Wrap spacing={2}>
              {keywordFilter?.trim() && (
                <WrapItem>
                  <Tag size="sm" variant="subtle" colorScheme="blue">
                    <TagLabel>Keyword: {keywordFilter.trim()}</TagLabel>
                    <TagCloseButton onClick={handleRemoveKeywordFilter} />
                  </Tag>
                </WrapItem>
              )}
              {statuses.map((status) => (
                <WrapItem key={status}>
                  <Tag size="sm" variant="subtle" colorScheme="blue">
                    <TagLabel>{getStatusLabel(status)}</TagLabel>
                    <TagCloseButton
                      onClick={() => handleRemoveStatusFilter(status)}
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Flex>
      </Box>
      <HStack justify="flex-end" flexShrink={0}>
        {csvButton}
        {sortSelect}
      </HStack>
      {listContent}
    </Flex>
  ) : (
    // Desktop layout - 12 column grid: filters (4 cols), list (8 cols)
    <Grid
      templateColumns="repeat(12, 1fr)"
      gap="4"
      flex="1"
      minH="0"
      overflow="hidden"
    >
      <GridItem colSpan={4} alignSelf="start">
        <Box
          w="100%"
          p={5}
          bg="gray.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          alignSelf="flex-start"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontSize="md" fontWeight="semibold" color="gray.900">
              Filters
            </Text>
            <Button
              size="sm"
              variant="ghost"
              color="blue.500"
              fontWeight="medium"
              px={2}
              isDisabled={!hasAppliedFilters}
              onClick={handleResetFilters}
              _hover={{
                bg: "transparent",
              }}
              _active={{
                bg: "transparent",
              }}
            >
              Reset
            </Button>
          </Flex>
          <Text fontSize="sm" color="gray.600" mt={1} mb={5}>
            Showing {totalAvailableForFilters} of {totalUnfilteredCount}
          </Text>
          <AiInterviewerCandidatesFiltersBody
            keyword={keywordFilter || ""}
            statuses={statuses}
            onKeywordChange={setKeywordFilter}
            onStatusesChange={handleStatusesChange}
            statusCounts={statusCounts}
            isSearching={isSearching}
          />
        </Box>
      </GridItem>
      <GridItem colSpan={8} minH="0" overflow="hidden">
        <Flex direction="column" gap="4" h="100%" minH="0">
          <HStack justify="space-between" flexShrink={0} alignItems="center">
            <Text fontSize="lg" fontWeight="semibold" color="gray.800">
              Candidates{" "}
              <Text as="span" fontWeight="normal" color="gray.500">
                ({totalAvailableForFilters})
              </Text>
            </Text>
            <HStack spacing="2">
              {csvButton}
              {sortSelect}
            </HStack>
          </HStack>
          {listContent}
        </Flex>
      </GridItem>
    </Grid>
  );
};

const MemoizedAiInterviewerCandidateScores = React.memo(
  AiInterviewerCandidateScores
);

export const AiInterviewerCandidates: React.FC<
  AiInterviewerCandidatesProps
> = ({ aiInterviewer }) => {
  const hasScorableQuestions = aiInterviewer.hasScorableQuestions ?? false;
  const { canEdit } = useBHScreenPermission(aiInterviewer);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const showIntegritySignals = useFeatureFlag(
    "ai_interviewer:interview_integrity:ui"
  );

  return (
    <Flex
      mt="3"
      direction="column"
      gap="4"
      flex="1"
      minH="0"
      minW="0"
      w="100%"
      overflow="hidden"
    >
      {!isMobile && (
        <MemoizedAiInterviewerCandidateScores aiInterviewer={aiInterviewer} />
      )}
      <AiInterviewerCandidatesList
        aiInterviewerId={aiInterviewer.id}
        aiInterviewerName={aiInterviewer.name}
        hasScorableQuestions={hasScorableQuestions}
        canEdit={canEdit}
        showIntegritySignals={showIntegritySignals}
      />
    </Flex>
  );
};
